/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { events } from '@adobe-commerce/event-bus';
import { fetchGraphQl } from '@/auth/api/fetch-graphql';
import { PermissionsModel } from '@/auth/data/models';
import { GET_CUSTOMER_ROLE_PERMISSIONS } from './graphql/getCustomerRolePermissions.graphql';

// TypeScript interfaces
interface Permission {
  id: string;
  text: string;
  children?: Permission[];
}

interface Role {
  id: string;
  name: string;
  permissions?: Permission[];
}

interface GetCustomerRolePermissionsResponse {
  data?: {
    customer?: {
      purchase_orders_enabled?: boolean;
      role?: Role;
    };
  };
  errors?: {
    message: string;
  }[];
}

// Module-level cache
let permissionsCache: PermissionsModel | null = null;
let fetchPromise: Promise<PermissionsModel> | null = null;

/**
 * Recursively flattens permission tree into a flat object
 */
const flattenPermissionTree = (
  permissions: Permission[]
): Record<string, boolean> => {
  const flattened: Record<string, boolean> = {};

  const processPermissions = (perms: Permission[]): void => {
    perms.forEach((permission) => {
      flattened[permission.id] = true;
      if (permission.children?.length) {
        processPermissions(permission.children);
      }
    });
  };

  processPermissions(permissions);
  return flattened;
};

/**
 * Purchase Order related permissions that should be set to false
 * when purchase orders are disabled
 */
const PURCHASE_ORDER_PERMISSIONS = [
  'Magento_PurchaseOrder::all',
  'Magento_PurchaseOrder::view_purchase_orders',
  'Magento_PurchaseOrder::view_purchase_orders_for_subordinates',
  'Magento_PurchaseOrder::view_purchase_orders_for_company',
  'Magento_PurchaseOrder::autoapprove_purchase_order',
  'Magento_PurchaseOrderRule::super_approve_purchase_order',
  'Magento_PurchaseOrderRule::view_approval_rules',
  'Magento_PurchaseOrderRule::manage_approval_rules',
];

/**
 * Determines if user has admin privileges
 * Admin is only when user has role ID 'MA==' AND has an empty permissions array
 */
const isAdminUser = (role?: Role): boolean => {
  return (
    role?.id === 'MA==' &&
    Array.isArray(role.permissions) &&
    role.permissions.length === 0
  );
};

/**
 * Gets all available permissions from the GraphQL response
 * Returns an empty object if no permissions (e.g., admin users have empty array)
 */
const getAllPermissions = (role?: Role): Record<string, boolean> => {
  if (role?.permissions?.length) {
    return flattenPermissionTree(role.permissions);
  }
  return {};
};

/**
 * Handles purchase order permissions based on PO enabled status
 * When PO is disabled, explicitly sets all PO permissions to false
 * This ensures consumers can distinguish between "no data" and "explicitly disabled"
 */
const overridePurchaseOrderPermissions = (
  permissions: Record<string, boolean>,
  purchaseOrdersEnabled?: boolean
): Record<string, boolean> => {
  // If PO is enabled, return permissions as-is from backend
  if (purchaseOrdersEnabled === true) {
    return permissions;
  }

  // When PO is disabled, explicitly set all PO permissions to false
  const result = { ...permissions };
  PURCHASE_ORDER_PERMISSIONS.forEach((permission) => {
    result[permission] = false;
  });

  return result;
};

/**
 * Processes role data into flattened permissions
 */
const processUserPermissions = (
  role?: Role,
  purchaseOrdersEnabled?: boolean
): PermissionsModel => {
  const isAdmin = isAdminUser(role);

  // Get all permissions (for both admin and non-admin users)
  const allPermissions = getAllPermissions(role);

  // Override PO permissions to false if PO is disabled
  const processedPermissions = overridePurchaseOrderPermissions(
    allPermissions,
    purchaseOrdersEnabled
  );

  const basePermissions: PermissionsModel = {
    all: true,
    ...(isAdmin && { admin: true }),
  };

  return { ...basePermissions, ...processedPermissions };
};

/**
 * Fetches user role permissions from GraphQL API
 */
const fetchUserRolePermissions = async (): Promise<PermissionsModel> => {
  try {
    const response = (await fetchGraphQl(GET_CUSTOMER_ROLE_PERMISSIONS, {
      method: 'GET',
    })) as GetCustomerRolePermissionsResponse;

    const permissions = processUserPermissions(
      response.data?.customer?.role,
      response.data?.customer?.purchase_orders_enabled
    );

    // Update cache
    permissionsCache = permissions;
    fetchPromise = null;

    return permissions;
  } catch (error) {
    fetchPromise = null;
    throw error;
  }
};

/**
 * Gets user role permissions with caching
 */
export const getCustomerRolePermissions = (): Promise<PermissionsModel> => {
  // Return cached data as resolved promise if available
  if (permissionsCache) {
    events.emit('auth/permissions', permissionsCache);
    return Promise.resolve(permissionsCache);
  }

  // No cache available - create and return fetch promise if not already fetching
  if (!fetchPromise) {
    fetchPromise = fetchUserRolePermissions().then((permissions) => {
      events.emit('auth/permissions', permissions);
      return permissions;
    });
  }

  return fetchPromise;
};

/**
 * Resets the permissions cache
 * @internal
 */
export const _resetCache = (): void => {
  permissionsCache = null;
  fetchPromise = null;
};
